/**
 * Global Commodities Page
 * Displays global commodity futures data with interactive charts
 * Supports multi-commodity overlays as per BRD FR-100
 */

import React, { useState, useRef, useMemo, useEffect } from 'react';
import PlanBanner from '../../components/PlanBanner';
import {
  Grid,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListSubheader,
  Chip,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Snackbar,
  Alert,
  Autocomplete,
  TextField,
} from '@mui/material';
import { Icon } from '@iconify/react';

import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ComposedChart,
} from 'recharts';
import { PageHeader, ChartCard, getChartStyles, chartColors, LoadingSkeleton, BenchmarkCard, DeltaChip, ExportButton, WorldReferencePanel, WorldAttribution, ChartTooltip } from '../../components/common';
import {
  useWorldReferenceBatch,
  useWorldSeriesCatalogue,
  useGlobalCommodities,
  useGlobalPrices,
  useFxSeries,
  useGlobalSpecies,
  useSpeciesPrices,
  useCommodities,
  useCategories,
  useLocalCommoditySeries,
} from '../../services/queries';
import { useThemeMode, MONO } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import {
  WORLD_MONTHS, WORLD_COPY, WORLD_UNITS, WORLD_CARD_SERIES,
  WORLD_TIME_RANGES, DEFAULT_WORLD_RANGE,
  DEFAULT_WORLD_SERIES, toPriceMap, toSeriesMap, toPickerOptions,
  exchangeFeedIsEmpty, monthLabel, unitLabelFor, kgPerPublishedUnit, cardLabel,
  attributionOf,
} from '../../utils/worldReference';
import { getPrefs } from '../../utils/preferences';
import { activeCommodities, activeCategories } from '../../utils/catalogStatus';
import { useRecipes } from '../../theme/recipes';

const ChevronLeft = (props) => <Icon icon="solar:alt-arrow-left-outline" width={18} height={18} {...props} />;
const ChevronRight = (props) => <Icon icon="solar:alt-arrow-right-outline" width={18} height={18} {...props} />;

// Shared monospace font for numeric values
const firaCode = { fontFamily: MONO };

// Chart colors for different commodities – use shared palette
const CHART_COLORS = {
  'GC001': chartColors[0], // Cocoa
  'GC002': chartColors[1], // Soybeans
  'GC003': chartColors[2], // Soymeal
  'GC004': chartColors[3], // Coffee
  'GC005': chartColors[4]  // Sugar
};

// Time range options
// Axis date label: day + month inside ~6 months of span, month + year beyond.
const fmtAxisDate = (d) => {
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};
// Day spans, cut by date (the exchange feed is daily; a point-count slice
// would misrepresent the period).
const TIME_RANGES = [
  { label: '1M', value: 31 },
  { label: '3M', value: 92 },
  { label: '6M', value: 183 },
  { label: '1Y', value: 366 },
  { label: 'ALL', value: 99999 }
];
const cutRangeByDate = (rows, days) => {
  if (days >= 99999) return rows || [];
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - days);
  const iso = cutoff.toISOString().slice(0, 10);
  return (rows || []).filter((r) => r.date >= iso);
};

// Format YYYY-MM-DD -> "18 Apr 2026"
const fmtDate = (d) => {
  if (!d) return '-';
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? '-' : dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

// Native unit → kilograms conversion per commodity.
// Bushel weights are commodity-specific; CBOT soymeal quotes in short tons.
// Sources: CME/ICE contract specs.
const KG_PER_NATIVE_UNIT = {
  GC001: 1000,     // Cocoa - USD/MT (metric ton)
  GC002: 27.2155,  // Soybeans - USD/Bushel (60 lb standard)
  GC003: 907.185,  // Soymeal - USD/short ton (CBOT convention)
  GC004: 0.453592, // Coffee - USD/lb
  GC005: 0.453592, // Sugar - USD/lb
};

// Convert a native-unit price to the requested display unit.
const convertPrice = (price, commodityId, targetUnit, kgPerNativeOverride) => {
  if (targetUnit === 'native') return price;
  // Species ids look like 'GC001-A' - fall back to the parent's kg factor so
  // sibling variants inherit the same per-unit conversion as their parent.
  const parentId = commodityId?.split('-')[0];
  // The override is for data whose unit is not an exchange contract, where
  // the caller knows the weight and this table does not.
  const kgPerNative = kgPerNativeOverride
    || KG_PER_NATIVE_UNIT[commodityId] || KG_PER_NATIVE_UNIT[parentId];
  if (!kgPerNative) return price;
  const pricePerKg = price / kgPerNative;
  if (targetUnit === 'kg') return pricePerKg;
  if (targetUnit === 'MT') return pricePerKg * 1000;
  if (targetUnit === 'lb') return pricePerKg * 0.453592;
  return price;
};

const CURRENCY_OPTIONS = [
  { value: 'USD', label: 'USD ($)', symbol: '$' },
  { value: 'NGN', label: 'NGN (₦)', symbol: '₦' },
];

function GlobalCommodities() {
  const { isDark, palette } = useThemeMode();
  const chartStyles = getChartStyles(isDark);
  const recipes = useRecipes();
  const { user: authUser } = useAuth();

  // Live data from the API (object-keyed series match the old mockData shapes).
  const { data: globalCommodities = [], isLoading: l1 } = useGlobalCommodities();
  const { data: exchangePrices = {}, isLoading: l2 } = useGlobalPrices('ALL');

  // The ICE and CBOT feed was a paid vendor trial that expired, so in
  // production that map is empty and every block below it renders blank.
  // When it is empty we serve the same surfaces from the World Bank Pink
  // Sheet instead, and relabel everything that claimed an exchange source.
  // When it has rows, nothing here changes: exchange data wins.
  const usingWorldReference = exchangeFeedIsEmpty(exchangePrices);

  // The catalogue first, because it says which series exist. Prices are then
  // fetched BY SERIES ID rather than by the five card ids, so the picker can
  // offer all 22 and the chart can draw any of them.
  const { data: worldCatalogue } = useWorldSeriesCatalogue({ enabled: usingWorldReference });
  const worldOptions = React.useMemo(() => toPickerOptions(worldCatalogue), [worldCatalogue]);
  const allSeriesIds = React.useMemo(() => worldOptions.map((o) => o.id), [worldOptions]);

  const { data: worldBatch } = useWorldReferenceBatch(
    allSeriesIds, WORLD_MONTHS, { enabled: usingWorldReference && allSeriesIds.length > 0 },
  );
  // Keyed by SERIES id now. In world mode that is the only id space: a card
  // for GC001 looks up the series standing in for it, so one selection model
  // covers both the cards and the picker.
  const worldPrices = React.useMemo(() => toPriceMap(worldBatch), [worldBatch]);
  const worldSeries = React.useMemo(() => toSeriesMap(worldBatch), [worldBatch]);
  const worldNotes = React.useMemo(() => {
    const out = {};
    const entries = worldBatch?.commodities || {};
    Object.keys(entries).forEach((id) => { out[id] = entries[id]?.discontinued || null; });
    return out;
  }, [worldBatch]);
  const worldAttribution = attributionOf(worldBatch);
  const globalCommodityPrices = usingWorldReference ? worldPrices : exchangePrices;

  // Weight unit for world figures. Tonne by default: the Pink Sheet quotes
  // cocoa per kilo, so $5.95 is the same price a futures screen calls $5,950,
  // and the small number reads as a collapse when it is not one.
  const [worldUnit, setWorldUnit] = useState('MT');

  // What the carousel shows. In world mode these are the crops Farmgate's
  // surveyors actually price, not the five exchange contracts we inherited,
  // four of which nobody here trades.
  const worldCards = React.useMemo(() => {
    const byId = Object.fromEntries(worldOptions.map((o) => [o.id, o]));
    return WORLD_CARD_SERIES.filter((id) => byId[id]).map((id) => {
      const { name, detail } = cardLabel(byId[id].label, byId[id].group);
      return { id, name, detail, fullName: byId[id].label, group: byId[id].group };
    });
  }, [worldOptions]);
  // In world mode the unit comes from the series, not the exchange contract:
  // the Pink Sheet quotes $/mt and $/kg, never bushels or pounds.
  const unitFor = (id) =>
    usingWorldReference
      ? unitLabelFor(worldSeries[id]?.unit, currency, worldUnit)
      : (currency === 'NGN' ? findItem(id)?.unit?.replace(/^USD/, 'NGN') : findItem(id)?.unit);
  const { data: fxRates = [], isLoading: l3 } = useFxSeries('ALL');
  const { data: globalCommoditySpecies = {}, isLoading: l4 } = useGlobalSpecies();
  const { data: globalSpeciesPrices = {}, isLoading: l5 } = useSpeciesPrices('ALL');
  const { data: localCommodities = [], isLoading: l6 } = useCommodities();
  const { data: commodityCategories = [], isLoading: l7 } = useCategories();
  const { data: localCommodityPrices = {}, isLoading: l8 } = useLocalCommoditySeries('ALL');
  const loading = l1 || l2 || l3 || l4 || l5 || l6 || l7 || l8;

  const [selectedCommodity, setSelectedCommodity] = useState('GC001');
  const [selectedLocalCategory, setSelectedLocalCategory] = useState(
    () => localCommodities[0]?.category || 'CAT001'
  );
  const [overlays, setOverlays] = useState([]);
  const [timeRange, setTimeRange] = useState(183);
  // Only relevant when comparing (overlays active). Default 'native' preserves the
  // familiar per-commodity unit when looking at a single line.
  const [normalizeUnit, setNormalizeUnit] = useState('native');
  const isComparing = overlays.length > 0;
  // Market mode (Global futures vs Nigerian local market) and Compare mode
  // (across commodities vs species/variants of the same commodity).
  const [marketMode, setMarketMode] = useState('global');
  const [compareMode, setCompareMode] = useState('commodities');
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const isLocal = marketMode === 'local';
  // World data is on screen for an exchange-traded commodity. Drives the
  // labels, the chart interpolation, the dropped volume tile and the table's
  // column count, so none of them can disagree with the others.
  const worldView = usingWorldReference && !isLocal;
  // How far back this series actually goes. The card had three thin rows and
  // a lot of white space, and "we hold 66 years of this" is the fact that
  // makes the free source look like the upgrade it is.
  const worldCoverage = React.useMemo(() => {
    const rows = worldPrices[selectedCommodity] || [];
    if (rows.length === 0) return null;
    return {
      from: monthLabel(rows[0].date),
      to: monthLabel(rows[rows.length - 1].date),
      months: rows.length,
    };
  }, [worldPrices, selectedCommodity]);
  // Picking a series whose data stopped years ago cannot show anything inside
  // a six month window, so the reader gets a warning and then four dashes.
  // Widen the range once, on the switch itself, so the history they were
  // warned about is actually on screen. Keyed on the series so it never
  // fights a range the reader chooses afterwards.
  // A day-count range from the exchange buttons is not one of the year
  // buttons world mode offers, so nothing would look selected. Snap it once.
  const worldRangeSetRef = useRef(false);
  useEffect(() => {
    if (!worldView) { worldRangeSetRef.current = false; return; }
    if (worldRangeSetRef.current) return;
    worldRangeSetRef.current = true;
    if (!WORLD_TIME_RANGES.some((r) => r.value === timeRange)) setTimeRange(DEFAULT_WORLD_RANGE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [worldView]);

  const lastSeriesRef = useRef(null);
  useEffect(() => {
    if (!worldView || lastSeriesRef.current === selectedCommodity) return;
    lastSeriesRef.current = selectedCommodity;
    const rows = worldPrices[selectedCommodity] || [];
    if (rows.length === 0) return;
    const newest = new Date(rows[rows.length - 1].date);
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - timeRange);
    if (timeRange < 99999 && newest < cutoff) setTimeRange(99999);
    // timeRange is read, not depended on: this fires when the series changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [worldView, selectedCommodity, worldPrices]);

  // World ids are series ids. A selection carried over from the exchange id
  // space, or from a series that has since gone, would resolve to nothing and
  // leave an empty chart with no explanation, so snap it to something real.
  useEffect(() => {
    if (!worldView || allSeriesIds.length === 0) return;
    if (!allSeriesIds.includes(selectedCommodity)) {
      setSelectedCommodity(
        allSeriesIds.includes(DEFAULT_WORLD_SERIES) ? DEFAULT_WORLD_SERIES : allSeriesIds[0],
      );
    }
  }, [worldView, allSeriesIds, selectedCommodity]);
  // World figures always carry the chosen weight unit, not only while
  // comparing: the whole point of the default is that the headline number
  // reads at a familiar magnitude.
  const effectiveUnit = worldView ? worldUnit : (isComparing ? normalizeUnit : 'native');
  // Look up the metadata for any id (global commodity, global species, or local commodity).
  const findItem = (id) =>
    // A world series is a first-class selection in world mode, so it has to
    // answer like a catalogue row.
    (usingWorldReference && worldOptions.find((o) => o.id === id)
      ? (() => {
          const o = worldOptions.find((x) => x.id === id);
          return { id: o.id, name: o.label, unit: o.unit, category: o.group };
        })()
      : null) ||
    globalCommodities.find((c) => c.id === id) ||
    Object.values(globalCommoditySpecies).flat().find((s) => s.id === id) ||
    localCommodities.find((c) => c.id === id);
  // Time series for any id (handles all three sources).
  const priceSeriesForId = (id) =>
    globalCommodityPrices[id] ||
    globalSpeciesPrices[id] ||
    localCommodityPrices[id] ||
    [];
  // Reset selection + overlays when market changes so we don't render mismatched ids.
  const handleMarketChange = (_, newMode) => {
    if (!newMode || newMode === marketMode) return;
    setMarketMode(newMode);
    setOverlays([]);
    if (newMode === 'global') {
      // In world mode the ids are series, so GC001 would select nothing.
      setSelectedCommodity(usingWorldReference ? DEFAULT_WORLD_SERIES : 'GC001');
    } else {
      const firstLocal = activeLocals[0] || localCommodities[0];
      setSelectedCommodity(firstLocal?.id || 'COM001');
      setSelectedLocalCategory(firstLocal?.category || 'CAT001');
    }
  };
  const handleLocalCategoryChange = (e) => {
    const newCat = e.target.value;
    setSelectedLocalCategory(newCat);
    const firstVariant = localCommodities.find((c) => c.category === newCat);
    if (firstVariant) {
      setSelectedCommodity(firstVariant.id);
      setOverlays([]);
    }
  };
  // Pickers offer ACTIVE commodities/categories only (inactive = retired
  // from view); display joins elsewhere still resolve retired names.
  const activeLocals = useMemo(
    () => activeCommodities(localCommodities, commodityCategories),
    [localCommodities, commodityCategories]
  );
  const localVariantsInCategory = useMemo(
    () => activeLocals.filter((c) => c.category === selectedLocalCategory),
    [selectedLocalCategory, activeLocals]
  );
  const localCategoriesWithVariants = useMemo(() => {
    const ids = new Set(activeLocals.map((c) => c.category));
    return activeCategories(commodityCategories).filter((cat) => ids.has(cat.id));
  }, [activeLocals, commodityCategories]);
  // Self-heal local-mode selections when the picked category/commodity turns
  // retired: snap to the first active category and its first variant.
  useEffect(() => {
    if (!isLocal || localCategoriesWithVariants.length === 0) return;
    const catOk = localCategoriesWithVariants.some((c) => c.id === selectedLocalCategory);
    const comOk = activeLocals.some((c) => c.id === selectedCommodity);
    if (!catOk || !comOk) {
      const cat = catOk ? selectedLocalCategory : localCategoriesWithVariants[0].id;
      const firstVariant = activeLocals.find((c) => c.category === cat);
      setSelectedLocalCategory(cat);
      if (firstVariant) setSelectedCommodity(firstVariant.id);
      setOverlays([]);
    }
  }, [isLocal, localCategoriesWithVariants, activeLocals, selectedLocalCategory, selectedCommodity]);
  const handleCompareModeChange = (_, newMode) => {
    if (!newMode || newMode === compareMode) return;
    setCompareMode(newMode);
    setOverlays([]); // overlay options shift with the comparison mode
  };
  // Currency toggle for the chart + stats panel. NGN multiplies by the per-week
  // historical NGN/USD rate (falls back to the latest rate for any miss).
  // The initial currency comes from the user's Settings preference.
  const [currency, setCurrency] = useState(() => getPrefs(authUser?.email).currency);
  const fxByDate = useMemo(() => {
    const m = {};
    fxRates.forEach((f) => { m[f.date] = f.rate; });
    return m;
  }, [fxRates]);
  const latestFxRate = fxRates[fxRates.length - 1]?.rate || 1550;
  const fxFor = (date) => (currency === 'NGN' ? (fxByDate[date] ?? latestFxRate) : 1);
  const currencySymbol = currency === 'NGN' ? '₦' : '$';

  // Carousel scroll controls for commodity cards row
  const cardScrollRef = useRef(null);
  const scrollCards = (dir) => {
    if (!cardScrollRef.current) return;
    const amount = 260;
    cardScrollRef.current.scrollBy({ left: dir * amount, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <Box>
        <PageHeader
          title="Commodities"
          subtitle="Track global and Nigerian commodity prices"
          breadcrumbs={[{ label: 'Home', path: '/' }, { label: 'Commodities' }]}
        />

      <PlanBanner adds={['export', 'unlimited alerts and holdings']} />
        <LoadingSkeleton variant="page" />
      </Box>
    );
  }

  // Resolve the focused commodity + its time series via the active market source.
  // Local commodities are denominated in NGN already, so we skip the FX multiplier
  // and the normalize conversion (which is keyed by global commodity ids).
  const currentCommodity = findItem(selectedCommodity);
  const priceData = priceSeriesForId(selectedCommodity);
  const filteredData = cutRangeByDate(priceData, timeRange);
  // For local-market series we treat the price as native NGN: no FX multiply,
  // no per-kg normalize. The Currency / Normalize selects are hidden in that mode.
  const localFx = (date) => 1;
  const fxForActive = isLocal ? localFx : fxFor;
  const convertPriceActive = (val, id) => {
    if (isLocal) return val;
    // A world series is published per kilo or per tonne, which the exchange
    // table of bushel and pound weights knows nothing about. Pass the right
    // factor rather than letting convertPrice fall through and return the
    // figure unconverted under a converted label.
    if (worldView) {
      const kgPerNative = kgPerPublishedUnit(worldSeries[id]?.unit);
      return convertPrice(val, id, effectiveUnit, kgPerNative);
    }
    return convertPrice(val, id, effectiveUnit);
  };
  const currencySymbolActive = isLocal ? '₦' : currencySymbol;

  // Prepare chart data with overlays. Apply normalize → currency conversion
  // per data point so axis values, tooltip values and stats stay in sync.
  const overlayCloseByDate = {};
  overlays.forEach((id) => {
    overlayCloseByDate[id] = new Map((priceSeriesForId(id) || []).map((p) => [p.date, p.close]));
  });

  const chartData = filteredData.map((item, index) => {
    const fx = fxForActive(item.date);
    const dataPoint = { date: item.date };
    // Undefined, not zero, when there is no close. `null * fx` is 0, which
    // recharts plots as a real point: the line dropped to the axis floor and
    // took the Y domain down with it, so a missing figure looked like the
    // market trading at nothing. Leaving the key out makes it a gap.
    // The overlay branch below already got this right.
    if (item.close != null) {
      dataPoint[selectedCommodity] = convertPriceActive(item.close, selectedCommodity) * fx;
    }

    // Joined BY DATE, not array index: series have different lengths and
    // holidays, so an index join would plot different dates on one x position.
    overlays.forEach(overlayId => {
      const close = overlayCloseByDate[overlayId]?.get(item.date);
      if (close != null) {
        dataPoint[overlayId] = convertPriceActive(close, overlayId) * fx;
      }
    });

    return dataPoint;
  });

  // Per-row converted values for stats (so latest/prev/high/low all reflect
  // the active normalize + currency selection).
  const convertedRows = filteredData.map((item) => {
    const fx = fxForActive(item.date);
    // Null in, null out. open/high/low/close are all nullable in the database
    // and the API passes nulls straight through, and `null * fx` is 0 in
    // JavaScript, so multiplying first turned "no figure" into a real looking
    // zero before anything downstream could notice.
    const conv = (v) => (v == null ? null : convertPriceActive(v, selectedCommodity) * fx);
    return {
      close: conv(item.close),
      high: conv(item.high),
      low: conv(item.low),
      volume: item.volume,
    };
  });

  // Null, never zero, when there is nothing to report. With the exchange feed
  // absent these fell back to 0 and the panel printed "$0.00" and
  // "+0.00 (0.00%)" as though the market had not moved, which is a price we
  // invented. The volume line below already had this right; the price lines
  // did not. The public commodities page makes the same choice.
  //
  // Filtering the nulls out before Math.max/min matters as much as the ??
  // below: Math.max(null, 5) is 5, but Math.max(null) is 0, so a column of
  // nothing became a confident zero.
  const closes = convertedRows.map((r) => r.close).filter((v) => v != null);
  const highs = convertedRows.map((r) => r.high).filter((v) => v != null);
  const lows = convertedRows.map((r) => r.low).filter((v) => v != null);

  const latestPrice = closes.length ? closes[closes.length - 1] : null;
  const previousPrice = closes.length > 1 ? closes[closes.length - 2] : null;
  // A previous price of zero cannot yield a percentage, so it is not a
  // previous price for this purpose. Treating it as one printed "(null%)"
  // next to a real change figure.
  const comparable = previousPrice != null && previousPrice !== 0;
  const priceChange = latestPrice != null && comparable ? latestPrice - previousPrice : null;
  const priceChangePercent =
    priceChange != null ? ((priceChange / previousPrice) * 100).toFixed(2) : null;
  // A monthly average has no intraday high or low, so when those fields are
  // absent the highest and lowest MONTH is the right meaning, and the tiles
  // are relabelled to say so. Falling back to the closes keeps them useful
  // without inventing an OHLC range the source never published.
  const highSource = highs.length ? highs : closes;
  const lowSource = lows.length ? lows : closes;
  const highPrice = highSource.length ? Math.max(...highSource) : null;
  const lowPrice = lowSource.length ? Math.min(...lowSource) : null;
  // The exchange feed does not supply volume; average only real values and
  // keep null when none exist so the UI shows '-' instead of a fake 0.
  const volumes = filteredData.map((d) => d.volume).filter((v) => v != null);
  const avgVolume = volumes.length ? Math.round(volumes.reduce((sum, v) => sum + v, 0) / volumes.length) : null;


  return (
    <Box>
      <PageHeader
        title="Commodities"
        subtitle={worldView ? "World reference and Nigerian commodity prices" : "Track global commodity futures prices and trends"}
        breadcrumbs={[
          { label: 'Home', path: '/' },
          { label: 'Commodities' }
        ]}
        actions={
          <ExportButton
            filename={`${currentCommodity?.name || 'commodity'}-series`}
            label="Export data"
            headers={['Date', 'Close']}
            rows={filteredData.map((r) => [r.date, r.close])}
          />
        }
      />

      <Grid container spacing={3}>
        {/* Commodity Cards Overview - horizontal carousel */}
        <Grid item xs={12}>
          <Box sx={{ position: 'relative' }}>
            {/* Top control bar - currency toggle on the left, scroll buttons on the right.
                Currency is global (affects cards + chart + stats), so it lives above the carousel. */}
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 1,
              mb: 1,
              flexWrap: 'wrap',
            }}>
              {/* One group, because space-between was spreading these two
                  to opposite ends of the row. Unit sits beside Currency
                  because it has the same reach: both restate every figure on
                  the page, cards included. */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                {worldView && (
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Unit</InputLabel>
                    <Select value={worldUnit} label="Unit" onChange={(e) => setWorldUnit(e.target.value)}>
                      {WORLD_UNITS.map((u) => (
                        <MenuItem key={u.value} value={u.value}>{u.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>Currency</InputLabel>
                  <Select
                    value={currency}
                    label="Currency"
                    onChange={(e) => setCurrency(e.target.value)}
                  >
                    {CURRENCY_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.75 }}>
              <IconButton
                onClick={() => scrollCards(-1)}
                size="small"
                sx={{
                  bgcolor: palette.card,
                  ...recipes.ghostIconSx,
                  // Only while the cards actually scroll: from lg they all
                  // fit, and an arrow that moves nothing is clutter.
                  display: { xs: 'inline-flex', lg: 'none' },
                }}
              >
                <ChevronLeft fontSize="small" />
              </IconButton>
              <IconButton
                onClick={() => scrollCards(1)}
                size="small"
                sx={{
                  bgcolor: palette.card,
                  ...recipes.ghostIconSx,
                  // Only while the cards actually scroll: from lg they all
                  // fit, and an arrow that moves nothing is clutter.
                  display: { xs: 'inline-flex', lg: 'none' },
                }}
              >
                <ChevronRight fontSize="small" />
              </IconButton>
              </Box>
            </Box>

            <Box
              ref={cardScrollRef}
              sx={{
                display: 'flex',
                gap: 2,
                overflowX: 'auto',
                overflowY: 'hidden',
                scrollSnapType: 'x mandatory',
                pb: 1,
                '&::-webkit-scrollbar': { display: 'none' },
                msOverflowStyle: 'none',
                scrollbarWidth: 'none',
              }}
            >
            {(worldView ? worldCards : globalCommodities).map((gc) => {
              // Only rows that actually carry a close. Checking that a row
              // exists is not the same as checking that it has a figure:
              // close is nullable, `null * fx` is 0, and the card printed that
              // zero as a price, with a -100% delta beside it.
              // In world mode the card IS a series, so its id needs no
              // translation: one id space for cards, picker and chart.
              const cardId = gc.id;
              const data = (globalCommodityPrices[cardId] || []).filter((r) => r.close != null);
              // No contract symbol or exchange while world data is showing.
              // "CC · ICE" above a World Bank figure is a false attribution.
              const cardSymbol = worldView ? undefined : gc.symbol;
              // The grade says more than the group: "Thailand 5%" tells you
              // which rice benchmark, "Grains" does not.
              const cardExchange = worldView ? (gc.detail || 'World Bank') : gc.exchange;
              const latestRow = data[data.length - 1];
              const prevRow = data[data.length - 2];
              // Honest pending state: no priced rows means no price, never $0.00.
              const pending = !latestRow;
              // Convert to the chosen unit, not just relabel to it. The card
              // prints cardUnit beside these figures, and a $/kg number under
              // a $/mt label is off by a thousand.
              const latest = latestRow
                ? convertPriceActive(latestRow.close, cardId) * fxFor(latestRow.date)
                : null;
              const prev = prevRow
                ? convertPriceActive(prevRow.close, cardId) * fxFor(prevRow.date)
                : null;
              const delta = latest != null && prev ? ((latest - prev) / prev) * 100 : null;
              const cardUnit = unitFor(cardId);
              return (
                <BenchmarkCard
                  key={gc.id}
                  name={gc.name}
                  symbol={cardSymbol}
                  exchange={cardExchange}
                  price={latest != null ? `${currencySymbol}${latest.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                  unit={cardUnit}
                  delta={delta != null ? Number(delta.toFixed(2)) : null}
                  sparkData={data.slice(-16)}
                  sparkColor={delta != null && delta < 0 ? palette.danger : palette.accent}
                  updatedAt={latestRow?.date}
                  stampText={worldView ? monthLabel(latestRow?.date) : undefined}
                  grow
                  // Always, not only in world mode: every card in this row
                  // renders the same default globe, so it never distinguished
                  // one from another, and the 40px it costs is the difference
                  // between "Soybeans" and "Soybea...".
                  hideIcon
                  pendingText={worldView ? 'Awaiting world reference' : 'Awaiting exchange feed'}
                  selected={cardId === selectedCommodity}
                  pending={pending}
                  onSelect={() => {
                    // Carousel is global-only - clicking while in Local market
                    // auto-switches back to Global so the click never feels dead.
                    if (isLocal) {
                      setMarketMode('global');
                      setOverlays([]);
                      setSnackbar({ open: true, message: 'Switched to Global Market' });
                    }
                    setSelectedCommodity(cardId);
                  }}
                />
              );
            })}
            </Box>
          </Box>
        </Grid>

        {/* Main Chart */}
        <Grid item xs={12} lg={9}>
          <ChartCard
            topBar={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={marketMode}
                  onChange={handleMarketChange}
                  aria-label="Market"
                  sx={recipes.toggleGroupSx}
                >
                  <ToggleButton value="global" sx={{ textTransform: 'none', px: 2 }}>Global Market</ToggleButton>
                  <ToggleButton value="local" sx={{ textTransform: 'none', px: 2 }}>Local Market</ToggleButton>
                </ToggleButtonGroup>
                {/* The chart picks its own commodity. The cards above are
                    an overview: reaching them means scrolling up and back
                    down, and they only cover five of the twenty two series.
                    It belongs in this control row, not against the chart
                    title, where it squeezed the source note onto two lines
                    and left the controls split across two rows. */}
                {worldView && worldOptions.length > 0 && (
                  <FormControl size="small" sx={{ minWidth: 210 }}>
                    <InputLabel>Commodity</InputLabel>
                    <Select
                      value={allSeriesIds.includes(selectedCommodity) ? selectedCommodity : ''}
                      label="Commodity"
                      onChange={(e) => setSelectedCommodity(e.target.value)}
                    >
                      {worldOptions.flatMap((opt, i, arr) => {
                        const isNewGroup = i === 0 || arr[i - 1].group !== opt.group;
                        const rows = isNewGroup
                          ? [<ListSubheader key={`h-${opt.group}`}>{opt.group}</ListSubheader>]
                          : [];
                        rows.push(
                          <MenuItem key={opt.id} value={opt.id}>
                            {opt.label}
                            {worldNotes[opt.id] && (
                              <Box component="span" sx={{ ml: 1, fontSize: '0.65rem', color: 'text.secondary' }}>
                                (ended)
                              </Box>
                            )}
                          </MenuItem>,
                        );
                        return rows;
                      })}
                    </Select>
                  </FormControl>
                )}
                {isLocal && (
                  <>
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                      <InputLabel>Local commodity</InputLabel>
                      <Select
                        value={selectedLocalCategory}
                        label="Local commodity"
                        onChange={handleLocalCategoryChange}
                      >
                        {localCategoriesWithVariants.map((cat) => (
                          <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                      <InputLabel>Species</InputLabel>
                      <Select
                        value={selectedCommodity}
                        label="Species"
                        onChange={(e) => { setSelectedCommodity(e.target.value); setOverlays([]); }}
                      >
                        {localVariantsInCategory.map((c) => (
                          <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </>
                )}
              </Box>
            }
            title={`${currentCommodity?.name || ''} ${isLocal ? 'Price Trend' : 'Price Chart'}`}
            titleSx={{
              fontWeight: 700,
              fontSize: { xs: '1.1rem', md: '1.3rem' },
              letterSpacing: '-0.01em',
              lineHeight: 1.25,
            }}
            subtitle={
              <Box component="span" sx={{ display: 'block' }}>
                <Box component="span" sx={{ display: 'block' }}>
                  {/* In world mode the title already IS the series name, so
                      repeating it here cost a line and crowded the controls
                      opposite. The group is new information. */}
                  {isLocal
                    ? (commodityCategories.find((cat) => cat.id === currentCommodity?.category)?.name || 'Local market')
                    : (usingWorldReference
                        ? (findItem(selectedCommodity)?.category || '')
                        : currentCommodity?.exchange)}
                </Box>
                <Box component="span" sx={{ display: 'block', fontSize: '0.7rem', color: 'text.secondary', opacity: 0.8, mt: 0.25 }}>
                  {isLocal
                    ? 'Source: Field Agents'
                    : (usingWorldReference ? WORLD_COPY.sourceLine : 'Source: ICE · CBOT')}
                  {!isLocal && usingWorldReference && <> · Monthly average, about a month in arrears</>}
                  {!isLocal && isComparing && normalizeUnit !== 'native' && (
                    <> · Normalized to {currency}/{normalizeUnit}</>
                  )}
                </Box>
                {worldView && worldNotes[selectedCommodity] && (
                  <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
                    <Alert severity="warning" sx={{ py: 0, fontSize: '0.72rem' }}>
                      {worldNotes[selectedCommodity]}
                    </Alert>
                  </Box>
                )}
                {!isLocal && currency === 'NGN' && (
                  <Box component="span" sx={{ display: 'block', fontSize: '0.7rem', color: 'text.secondary', opacity: 0.8, mt: 0.15 }}>
                    Converted at NGN/USD ≈ {Math.round(latestFxRate)}
                  </Box>
                )}
              </Box>
            }
            height={540}
            actions={
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                {!isLocal && !worldView && isComparing && (
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Normalize</InputLabel>
                    <Select
                      value={normalizeUnit}
                      label="Normalize"
                      onChange={(e) => setNormalizeUnit(e.target.value)}
                    >
                      <MenuItem value="native">Native</MenuItem>
                      <MenuItem value="kg">{currency}/kg</MenuItem>
                      <MenuItem value="MT">{currency}/MT</MenuItem>
                      <MenuItem value="lb">{currency}/lb</MenuItem>
                    </Select>
                  </FormControl>
                )}
                <Box sx={{ display: 'flex', gap: 0.75 }}>
                  {(worldView ? WORLD_TIME_RANGES : TIME_RANGES).map((range) => (
                    <Button
                      key={range.label}
                      size="small"
                      variant="outlined"
                      onClick={() => setTimeRange(range.value)}
                      sx={recipes.rangeBtnSx(timeRange === range.value)}
                    >
                      {range.label}
                    </Button>
                  ))}
                </Box>
              </Box>
            }
          >
            {/* Price Summary */}
            <Box sx={{ display: 'flex', gap: 4, mb: 2, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Current Price</Typography>
                <Typography variant="h5" fontWeight={700} sx={firaCode}>
                  {latestPrice == null
                    ? '-'
                    : `${currencySymbolActive}${latestPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1, ...firaCode }}>
                    {worldView
                      ? unitFor(selectedCommodity)
                      : effectiveUnit === 'native'
                        ? (currency === 'NGN'
                            ? currentCommodity?.unit?.replace(/^USD/, 'NGN')
                            : currentCommodity?.unit)
                        : `${currency}/${normalizeUnit}`}
                  </Typography>
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {usingWorldReference && !isLocal ? WORLD_COPY.changeLabel : 'Change (7-day)'}
                </Typography>
                <Typography
                  variant="h5"
                  fontWeight={700}
                  color={priceChange == null ? 'text.secondary' : (priceChange >= 0 ? 'success.main' : 'error.main')}
                  sx={firaCode}
                >
                  {/* Two decimals reads as "-0.00" on low-priced contracts;
                      widen the precision until the move is actually visible. */}
                  {priceChange == null
                    ? '-'
                    : `${priceChange >= 0 ? '+' : ''}${priceChange.toFixed(Math.abs(priceChange) >= 0.01 || priceChange === 0 ? 2 : 4)} (${priceChangePercent}%)`}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {usingWorldReference && !isLocal ? WORLD_COPY.highLabel : 'Period High'}
                </Typography>
                <Typography variant="h6" fontWeight={600} sx={firaCode}>{highPrice == null ? '-' : `${currencySymbolActive}${highPrice.toFixed(2)}`}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {usingWorldReference && !isLocal ? WORLD_COPY.lowLabel : 'Period Low'}
                </Typography>
                <Typography variant="h6" fontWeight={600} sx={firaCode}>{lowPrice == null ? '-' : `${currencySymbolActive}${lowPrice.toFixed(2)}`}</Typography>
              </Box>
              {!(usingWorldReference && !isLocal) && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Avg Volume</Typography>
                  <Typography variant="h6" fontWeight={600} sx={firaCode}>{avgVolume != null ? avgVolume.toLocaleString() : '-'}</Typography>
                </Box>
              )}
            </Box>

            {/* Chart */}
            <ResponsiveContainer width="100%" height={400}>
              <ComposedChart data={chartData}>
                <CartesianGrid {...chartStyles.grid} />
                <XAxis dataKey="date" {...chartStyles.axis} minTickGap={26} tickFormatter={fmtAxisDate} />
                <YAxis
                  {...chartStyles.axis}
                  domain={['auto', 'auto']}
                  tickFormatter={(v) => `${currencySymbolActive}${v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v.toFixed(2)}`}
                />
                {/* Rows sorted by value, so the tooltip order matches the
                    order the lines sit in on screen, and each row carries its
                    line's colour. */}
                <Tooltip
                  cursor={{ stroke: palette.divider }}
                  content={
                    <ChartTooltip
                      highlightKey={selectedCommodity}
                      labelFormatter={(d) => (worldView ? monthLabel(d) : fmtDate(d))}
                      nameFormatter={(name) => findItem(name)?.name || name}
                      valueFormatter={(v) => `${currencySymbolActive}${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                    />
                  }
                />
                <Legend />

                {/* Main commodity line */}
                <Line
                  type={worldView ? "stepAfter" : "monotone"}
                  dataKey={selectedCommodity}
                  stroke={CHART_COLORS[selectedCommodity] || palette.accent}
                  strokeWidth={2.5}
                  dot={false}
                  name={currentCommodity?.name}
                />

                {/* Overlay lines */}
                {overlays.map((overlayId, idx) => {
                  const overlay = findItem(overlayId);
                  const stroke = CHART_COLORS[overlayId] || chartColors[(idx + 1) % chartColors.length];
                  return (
                    <Line
                      key={overlayId}
                      type={worldView ? "stepAfter" : "monotone"}
                      dataKey={overlayId}
                      stroke={stroke}
                      strokeWidth={1.5}
                      strokeDasharray="5 5"
                      dot={false}
                      name={overlay?.name}
                    />
                  );
                })}
              </ComposedChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        {/* Sidebar Controls */}
        <Grid item xs={12} lg={3}>
          {/* Overlay Selector */}
          <Card sx={{ mb: 2 }}>
            <CardHeader
              title="Chart Overlays"
              titleTypographyProps={{ variant: 'subtitle1' }}
              subheader={
                compareMode === 'species'
                  ? (isLocal ? 'Compare variants in this category' : 'Compare species of this commodity')
                  : (isLocal ? 'Compare with other local commodities' : 'Compare with other commodities')
              }
            />
            <CardContent sx={{ pt: 0 }}>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={compareMode}
                onChange={handleCompareModeChange}
                aria-label="Compare mode"
                fullWidth
                sx={{ ...recipes.toggleGroupSx, mb: 1.5 }}
              >
                <ToggleButton value="commodities" sx={{ textTransform: 'none', flex: 1 }}>Commodities</ToggleButton>
                <ToggleButton value="species" sx={{ textTransform: 'none', flex: 1 }}>Species</ToggleButton>
              </ToggleButtonGroup>
              {(() => {
                // Build the list of items eligible to overlay, based on the
                // active market + comparison mode.
                let options = [];
                // In world mode the comparable things are the world series,
                // all 22 of them, not the five exchange contracts. Offering
                // the old catalogue meant picking a commodity whose id the
                // chart no longer knows, so the overlay drew nothing.
                if (compareMode === 'commodities') {
                  options = (isLocal
                    ? localCommodities
                    : (worldView ? worldOptions.map((o) => ({ id: o.id, name: o.label })) : globalCommodities)
                  ).filter((c) => c.id !== selectedCommodity);
                } else if (isLocal) {
                  const cat = currentCommodity?.category;
                  options = cat
                    ? activeLocals.filter((c) => c.id !== selectedCommodity && c.category === cat)
                    : [];
                } else {
                  options = (globalCommoditySpecies[selectedCommodity] || [])
                    .filter((sp) => (priceSeriesForId(sp.id) || []).length > 0);
                }
                if (options.length === 0) {
                  return (
                    <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
                      No comparable {compareMode === 'species' ? 'variants' : 'commodities'} available.
                    </Typography>
                  );
                }
                const selected = options.filter((o) => overlays.includes(o.id));
                return (
                  <Autocomplete
                    multiple
                    size="small"
                    options={options}
                    value={selected}
                    onChange={(_, picks) => setOverlays(picks.map((p) => p.id))}
                    getOptionLabel={(o) => o.name}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={selected.length === 0 ? `Add ${compareMode === 'species' ? 'variants' : 'commodities'} to compare` : ''}
                      />
                    )}
                    renderOption={(props, option, idx) => {
                      const stroke = CHART_COLORS[option.id] || chartColors[(idx.index + 1) % chartColors.length];
                      return (
                        <li {...props} key={option.id}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: stroke, mr: 1.2, flexShrink: 0 }} />
                          <Typography variant="body2">{option.name}</Typography>
                        </li>
                      );
                    }}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const stroke = CHART_COLORS[option.id] || chartColors[(index + 1) % chartColors.length];
                        const tagProps = getTagProps({ index });
                        return (
                          <Chip
                            size="small"
                            label={option.name}
                            {...tagProps}
                            key={option.id}
                            sx={{
                              bgcolor: `${stroke}22`,
                              border: `1px solid ${stroke}66`,
                            }}
                          />
                        );
                      })
                    }
                  />
                );
              })()}
            </CardContent>
          </Card>

          {/* Commodity Info */}
          <Card>
            <CardHeader
              title="Commodity Details"
              titleTypographyProps={{ variant: 'subtitle1' }}
            />
            <CardContent sx={{ pt: 0 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {usingWorldReference && !isLocal ? (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Series</Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {worldSeries[selectedCommodity]?.label || '-'}
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Symbol</Typography>
                      <Typography variant="body1" fontWeight={600}>{currentCommodity?.symbol}</Typography>
                    </Box>
                    <Divider />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Exchange</Typography>
                      <Typography variant="body1" fontWeight={600}>{currentCommodity?.exchange}</Typography>
                    </Box>
                  </>
                )}
                <Divider />
                <Box>
                  <Typography variant="caption" color="text.secondary">Unit</Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {(usingWorldReference && !isLocal ? unitFor(selectedCommodity) : currentCommodity?.unit) || '-'}
                  </Typography>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="caption" color="text.secondary">Category</Typography>
                  <Typography variant="body1" fontWeight={600}>{commodityCategories.find((c) => c.id === currentCommodity?.category)?.name || currentCommodity?.category || '-'}</Typography>
                </Box>
                {worldView && worldCoverage && (
                  <>
                    <Divider />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Published history</Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {worldCoverage.from} to {worldCoverage.to}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {worldCoverage.months.toLocaleString()} monthly figures
                      </Typography>
                    </Box>
                  </>
                )}
                {worldView && worldAttribution && (
                  <>
                    <Divider />
                    {/* CC BY 4.0 obliges the credit wherever the data shows,
                        and this page shows it throughout. */}
                    <WorldAttribution text={worldAttribution} />
                  </>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Only for local commodities. A global commodity already is a world
            price, so a world reference beside it would be the same number
            twice.

            It gets a Grid row of its own rather than sitting inside the chart
            column above. Grid items on one row are stretched to that row's
            height, which is fixed once, and this panel's chart is drawn by
            recharts a beat later: growing inside a stretched item made it
            overflow the column and land on top of the price history table
            below. Its own row has no such height to outgrow. */}
        {isLocal && (
          <Grid item xs={12} lg={9}>
            <WorldReferencePanel commodityId={selectedCommodity} />
          </Grid>
        )}

        {/* Price History Table */}
        <Grid item xs={12}>
          <Card>
            <CardHeader
              title={worldView ? 'Recent Monthly Averages' : 'Recent Price History'}
              titleTypographyProps={{ variant: 'h6' }}
              action={
                <ExportButton
                  filename={`${currentCommodity?.name || 'commodity'}-history`}
                  label="Export CSV"
                  headers={
                    worldView
                      ? ['Month', `Monthly average (${unitFor(selectedCommodity) || ''})`, 'Source']
                      : ['Date', 'Open', 'High', 'Low', 'Close', 'Volume']
                  }
                  rows={
                    worldView
                      ? filteredData.map((r) => [monthLabel(r.date), r.close ?? '', 'World Bank Pink Sheet'])
                      : filteredData.map((r) => [r.date, r.open, r.high, r.low, r.close, r.volume ?? ''])
                  }
                />
              }
            />
            <CardContent sx={{ p: 0 }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {(worldView
                        ? ['Month', 'Monthly average', 'Change']
                        : ['Date', 'Open', 'High', 'Low', 'Close', 'Volume', 'Change']
                      ).map((col, i) => (
                        <TableCell key={col} align={i === 0 ? 'left' : 'right'} sx={recipes.headCellSx}>
                          {col}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredData.slice(-10).reverse().map((row, index, arr) => {
                      // Every other cell in this row already refuses to invent
                      // a figure; the change column was the one that still did.
                      // With a null close it computed (null - prev) / prev and
                      // printed a confident -100.00% chip.
                      const prevClose = arr[index + 1]?.close;
                      const change =
                        row.close != null && prevClose != null && prevClose !== 0
                          ? (((row.close - prevClose) / prevClose) * 100).toFixed(2)
                          : null;
                      // Same conversion as the summary above: ONE currency
                      // story per screen (unit + FX applied everywhere).
                      const conv = (v) => (v != null ? convertPriceActive(v, selectedCommodity) * fxForActive(row.date) : null);
                      const fmtCell = (v) => (v != null ? `${currencySymbolActive}${conv(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-');
                      return (
                        <TableRow key={row.date} hover sx={recipes.bodyRowSx}>
                          {/* A monthly average has no open, high, low or
                              volume, so those columns are not printed as
                              dashes: they do not exist for this data. */}
                          <TableCell sx={{ ...firaCode, color: palette.textMuted, fontSize: '0.8rem' }}>
                            {worldView ? monthLabel(row.date) : fmtDate(row.date)}
                          </TableCell>
                          {!worldView && <TableCell align="right" sx={firaCode}>{fmtCell(row.open)}</TableCell>}
                          {!worldView && <TableCell align="right" sx={firaCode}>{fmtCell(row.high)}</TableCell>}
                          {!worldView && <TableCell align="right" sx={firaCode}>{fmtCell(row.low)}</TableCell>}
                          <TableCell align="right" sx={{ fontWeight: 600, ...firaCode }}>{fmtCell(row.close)}</TableCell>
                          {!worldView && <TableCell align="right" sx={firaCode}>{row.volume != null ? row.volume.toLocaleString() : '-'}</TableCell>}
                          <TableCell align="right">
                            {change == null
                              ? <Box component="span" sx={{ color: palette.textMuted }}>-</Box>
                              : <DeltaChip value={Number(change)} decimals={2} size="sm" />}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={2200}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="info"
          variant="filled"
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default GlobalCommodities;
